/* ==========================================================================
   Kairo — JS runtime
   Modern vanilla JavaScript. No build step, no dependencies, no transpile.
   Exposes a global `Kairo` namespace and auto-wires data attributes.

   APIs:
     Kairo.Modal.open(target)     — target: selector, bare ID, or element
     Kairo.Modal.close(target)    — target: selector, bare ID, or element
     Kairo.Drawer.open / .close   — alias of Modal, reads better for drawers
     Kairo.Select.init()          — upgrade [data-kr-select] native selects
     Kairo.Select.sync(target)    — resync visible UI after external value change
     Kairo.theme.set("dark"|"light"|"auto")  — override OS theme, persists
     Kairo.theme.get() / .resolved() / .toggle()
     Kairo.theme.setPreset("ocean"|"forest"|"rose"|"sunset"|"default")
     Kairo.theme.getPreset() / .presets
     Kairo.toast(message, options?)
     Kairo.confirm(message | options) -> Promise<boolean>

   Data attribute wiring (automatic):
     <button data-kr-open="myModalId">Open</button>
     <button data-kr-close>Close (inside a dialog)</button>
     <select data-kr-select>…</select>
   ========================================================================== */

(function (window) {
  "use strict";

  const DOC = window.document;

  /* -- Scroll lock fallback ------------------------------------------------
     The CSS uses :has() selectors (e.g. html:has(dialog.kr-modal[open]))
     to lock page scroll. This JS class toggle provides the same behaviour
     for browsers that lack :has() support. Both coexist safely. */
  function syncScrollLock() {
    var mobile = window.matchMedia("(max-width: 47.9375em)").matches;
    var locked =
      DOC.querySelector("dialog.kr-modal[open], dialog.kr-drawer[open], dialog.kr-command[open]") ||
      (mobile && DOC.querySelector('.kr-app[data-sidebar="open"]')) ||
      (mobile && DOC.querySelector("details.kr-menu[open]"));
    DOC.documentElement.classList.toggle("kr-scroll-lock", !!locked);
  }

  /**
   * Resolve a target to an element. Accepts:
   *   - an Element (returned as-is)
   *   - a bare ID ("myModal")
   *   - a CSS selector ("#myModal", ".foo", "[data-x]")
   * Invalid selectors never throw — they resolve to null so a single bad
   * attribute can't break the click delegation for the whole page.
   */
  function resolveElement(target) {
    if (!target) return null;
    if (target.nodeType === 1) return target;
    if (typeof target !== "string") return null;

    const trimmed = target.trim();
    if (!trimmed) return null;

    /* Bare ID token (common unescaped form). Prefer getElementById because
       it handles IDs starting with digits without escaping.
       If no such ID exists, fall through and try querySelector so simple
       selectors like "dialog" still work. */
    if (/^[A-Za-z0-9_-]+$/.test(trimmed)) {
      const byId = DOC.getElementById(trimmed);
      if (byId) return byId;
    }

    /* Otherwise treat as a selector. Invalid selectors throw a SyntaxError
       in querySelector; swallow it so the caller just gets null. */
    try {
      return DOC.querySelector(trimmed);
    } catch (_err) {
      return null;
    }
  }

  /* ==========================================================================
     Theme — light / dark / auto, with localStorage persistence
     ========================================================================== */

  const THEME_STORAGE_KEY = "kairo-theme";
  const PRESET_STORAGE_KEY = "kairo-preset";
  const THEME_VALUES = ["light", "dark", "auto"];
  const PRESET_VALUES = ["default", "ocean", "forest", "rose", "sunset"];
  const isStoredTheme = (v) => v === "dark" || v === "light";
  const isPreset = (v) => PRESET_VALUES.indexOf(v) > 0; /* skip "default" */

  /* Restore persisted theme + preset as early as possible. For zero FOUC,
     pages should inline this block in <head> before any CSS loads; loading
     kairo.js at bottom-of-body is too late. */
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isStoredTheme(stored)) {
      DOC.documentElement.setAttribute("data-theme", stored);
    }
    const storedPreset = window.localStorage.getItem(PRESET_STORAGE_KEY);
    if (isPreset(storedPreset)) {
      DOC.documentElement.setAttribute("data-kr-preset", storedPreset);
    }
  } catch (_err) {
    /* localStorage unavailable (Safari private mode, disabled cookies…) */
  }

  const darkMediaQuery =
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;

  const theme = {
    get() {
      const attr = DOC.documentElement.getAttribute("data-theme");
      return isStoredTheme(attr) ? attr : "auto";
    },

    resolved() {
      const explicit = theme.get();
      if (explicit !== "auto") return explicit;
      return darkMediaQuery && darkMediaQuery.matches ? "dark" : "light";
    },

    set(value) {
      const next = THEME_VALUES.includes(value) ? value : "auto";
      if (next === theme.get()) return;   /* no-op — skip storage + dispatch */

      if (next === "auto") {
        DOC.documentElement.removeAttribute("data-theme");
        try { window.localStorage.removeItem(THEME_STORAGE_KEY); } catch (_) {}
      } else {
        DOC.documentElement.setAttribute("data-theme", next);
        try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch (_) {}
      }
      DOC.documentElement.dispatchEvent(
        new CustomEvent("kr:theme:change", {
          bubbles: true,
          detail: { theme: next, resolved: theme.resolved() },
        })
      );
    },

    toggle() {
      theme.set(theme.resolved() === "dark" ? "light" : "dark");
    },

    /** Available color presets */
    presets: PRESET_VALUES,

    /** Get the active preset name ("default" if none) */
    getPreset() {
      const attr = DOC.documentElement.getAttribute("data-kr-preset");
      return isPreset(attr) ? attr : "default";
    },

    /** Set a color preset. "default" removes the override. */
    setPreset(name) {
      const next = PRESET_VALUES.includes(name) ? name : "default";
      if (next === theme.getPreset()) return;

      if (next === "default") {
        DOC.documentElement.removeAttribute("data-kr-preset");
        try { window.localStorage.removeItem(PRESET_STORAGE_KEY); } catch (_) {}
      } else {
        DOC.documentElement.setAttribute("data-kr-preset", next);
        try { window.localStorage.setItem(PRESET_STORAGE_KEY, next); } catch (_) {}
      }
      DOC.documentElement.dispatchEvent(
        new CustomEvent("kr:preset:change", {
          bubbles: true,
          detail: { preset: next },
        })
      );
    },
  };

  /* Re-broadcast when OS preference changes while in "auto" mode */
  if (darkMediaQuery && typeof darkMediaQuery.addEventListener === "function") {
    darkMediaQuery.addEventListener("change", () => {
      if (theme.get() === "auto") {
        DOC.documentElement.dispatchEvent(
          new CustomEvent("kr:theme:change", {
            bubbles: true,
            detail: { theme: "auto", resolved: theme.resolved() },
          })
        );
      }
    });
  }

  /* ==========================================================================
     Modal — native <dialog> wrapper
     ========================================================================== */

  /**
   * Find the best element to receive focus when a dialog opens (modal or
   * drawer). Priority: [autofocus] > first input > primary action > first
   * non-close button. Avoids focusing the close button when something more
   * meaningful exists, which also prevents the focus ring from appearing
   * on open.
   */
  function findBestFocusTarget(dialog) {
    /* 1. Explicit autofocus */
    const autofocus = dialog.querySelector("[autofocus]");
    if (autofocus && !autofocus.disabled) return autofocus;

    /* 2. First visible form field in the body (exclude hidden source selects
       from data-kr-select upgrade — those have aria-hidden + tabindex -1) */
    const field = dialog.querySelector(
      ":is(.kr-modal__body, .kr-drawer__body) :is(input:not([type=hidden]), textarea, select, summary.kr-select-menu__trigger):not([disabled]):not([aria-hidden='true'])"
    );
    if (field) return field;

    /* 3. Primary / danger action in the footer */
    const primary = dialog.querySelector(
      ":is(.kr-modal__footer, .kr-drawer__footer) :is(.kr-btn--primary, .kr-btn--danger):not([disabled])"
    );
    if (primary) return primary;

    /* 4. First non-close button anywhere */
    const btn = dialog.querySelector(
      ":is(.kr-modal__footer, .kr-modal__body, .kr-drawer__footer, .kr-drawer__body) button:not([disabled])"
    );
    if (btn) return btn;

    /* 5. Fallback: let the browser handle it */
    return null;
  }

  /* Returns "modal" | "drawer" | null. Null means the dialog is not managed
     by Kairo; all handlers short-circuit on null to leave it untouched. */
  function dialogKind(el) {
    if (!el || !el.classList) return null;
    if (el.classList.contains("kr-drawer")) return "drawer";
    if (el.classList.contains("kr-modal"))  return "modal";
    return null;
  }

  const Modal = {
    /**
     * Open a dialog using the native showModal() API.
     * Works on any Kairo-managed dialog (.kr-modal or .kr-drawer).
     * @param {string|HTMLDialogElement} target  Selector or <dialog> element.
     */
    open(target) {
      const el = resolveElement(target);
      if (!el || typeof el.showModal !== "function") return false;
      if (el.open) return true;
      el.showModal();
      syncScrollLock();
      /* Override the native auto-focus (which lands on first tabbable,
         often the close button) with a smarter target. */
      const focus = findBestFocusTarget(el);
      if (focus) {
        window.requestAnimationFrame(() => focus.focus());
      }
      const kind = dialogKind(el);
      if (kind) {
        el.dispatchEvent(new CustomEvent("kr:" + kind + ":open", { bubbles: true }));
      }
      return true;
    },

    /**
     * Close a dialog.
     * @param {string|HTMLDialogElement} target  Selector or <dialog> element.
     * @param {*} [returnValue]  Optional value returned via dialog.close().
     *
     * NOTE: the `kr:*:close` custom event is NOT dispatched here — it is
     * emitted by a document-level capture listener below that fires for ALL
     * close paths (ESC, programmatic `.close()`, backdrop click, method="dialog"
     * form submit). Dispatching here would either miss those paths or fire
     * twice for the ones that do go through this method.
     */
    close(target, returnValue) {
      const el = resolveElement(target);
      if (!el || typeof el.close !== "function") return false;
      if (!el.open) return true;
      el.close(returnValue);
      return true;
    },
  };

  /* -- Unified close event -------------------------------------------------
     The native `close` event on <dialog> fires for every close path but
     does not bubble, so we listen in the capture phase. This is the single
     point where `kr:modal:close` / `kr:drawer:close` is emitted — guarantees
     consumers receive it regardless of how the dialog was closed. */
  DOC.addEventListener("close", (event) => {
    const dialog = event.target;
    if (!(dialog instanceof window.HTMLDialogElement)) return;
    const kind = dialogKind(dialog);
    if (!kind) return;
    dialog.dispatchEvent(new CustomEvent("kr:" + kind + ":close", { bubbles: true }));
    syncScrollLock();
  }, true);

  /* -- Event delegation: data-kr-open / data-kr-close ---------------------- */
  DOC.addEventListener("click", (event) => {
    /* Open trigger */
    const opener = event.target.closest("[data-kr-open]");
    if (opener) {
      event.preventDefault();
      /* Treat the attribute value as a bare ID — use getElementById so an
         ID starting with a digit or containing other CSS meta characters
         doesn't cause querySelector to throw and break the whole page. */
      const id = (opener.getAttribute("data-kr-open") || "").trim();
      if (id) {
        const target = DOC.getElementById(id);
        if (target) Modal.open(target);
      }
      return;
    }

    /* Close trigger (button inside a dialog) */
    const closer = event.target.closest("[data-kr-close]");
    if (closer) {
      const dialog = closer.closest("dialog");
      if (dialog) Modal.close(dialog);
      return;
    }

    /* Alert dismiss — remove the nearest .kr-alert from the DOM */
    const alertCloser = event.target.closest("[data-kr-alert-close]");
    if (alertCloser) {
      const alert = alertCloser.closest(".kr-alert");
      if (alert) alert.remove();
    }

    /* Badge dismiss — remove the nearest .kr-badge (dismissible chip) */
    const badgeCloser = event.target.closest("[data-kr-badge-close]");
    if (badgeCloser) {
      const badge = badgeCloser.closest(".kr-badge");
      if (badge) {
        badge.dispatchEvent(new CustomEvent("kr:badge:dismiss", { bubbles: true }));
        badge.remove();
      }
    }
  });

  /* -- Click on backdrop closes the dialog --------------------------------
     Native <dialog> does not auto-close on backdrop click. We detect a click
     whose target is the dialog itself (bubbles from the backdrop) and whose
     coordinates are outside the content box. Works for both .kr-modal and
     .kr-drawer — whatever the drawer's edge or size. */
  DOC.addEventListener("click", (event) => {
    const dialog = event.target;
    if (!(dialog instanceof window.HTMLDialogElement)) return;
    if (!dialogKind(dialog)) return;
    if (!dialog.open) return;

    /* Ignore clicks inside the dialog content. Dialog's rect covers the
       content area (not the backdrop). */
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left && event.clientX <= rect.right &&
      event.clientY >= rect.top  && event.clientY <= rect.bottom;
    if (!inside) Modal.close(dialog);
  });

  /* ==========================================================================
     Toast — dynamic notifications
     ========================================================================== */

  const toastContainers = {};

  const TOAST_POSITIONS = [
    "top-right", "top-left", "top-center",
    "bottom-right", "bottom-left", "bottom-center",
  ];

  function ensureToastContainer(position) {
    const pos = position || "top-right";
    if (toastContainers[pos] && toastContainers[pos].isConnected) return toastContainers[pos];
    const el = DOC.createElement("div");
    el.className = "kr-toast-container kr-toast-container--" + pos;
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-atomic", "false");
    DOC.body.appendChild(el);
    toastContainers[pos] = el;
    return el;
  }

  /**
   * Display a toast notification.
   * @param {string} message
   * @param {object} [options]
   * @param {'info'|'success'|'warning'|'error'} [options.variant='info']
   * @param {number} [options.duration]  Auto-dismiss ms. 0 = manual. Default: scales with message length (min 3s, max 8s).
   * @param {string} [options.title]     Optional title above the message.
   * @param {string} [options.position]  Container position: top-right (default), top-left, top-center, bottom-right, bottom-left, bottom-center.
   * @returns {{ dismiss: () => void }}
   */
  function toast(message, options) {
    const opts = options || {};
    const variant = opts.variant || "info";
    const title = opts.title || null;
    const position = opts.position || "top-right";

    /* Scale duration with message length if not explicitly set */
    const duration = opts.duration !== undefined
      ? opts.duration
      : Math.min(8000, Math.max(3000, message.length * 50));

    const container = ensureToastContainer(position);

    const el = DOC.createElement("div");
    el.className = "kr-toast kr-toast--" + variant;
    el.setAttribute("role", variant === "error" ? "alert" : "status");

    const content = DOC.createElement("div");
    content.className = "kr-toast__content";

    if (title) {
      const titleEl = DOC.createElement("strong");
      titleEl.className = "kr-toast__title";
      titleEl.textContent = title;
      content.appendChild(titleEl);
    }

    const bodyEl = DOC.createElement("div");
    bodyEl.className = "kr-toast__body";
    bodyEl.textContent = message;
    content.appendChild(bodyEl);
    el.appendChild(content);

    /* Leave the button empty — CSS :empty::before renders the SVG X.
       aria-label carries the accessible name for screen readers. */
    const closeBtn = DOC.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "kr-toast__close";
    closeBtn.setAttribute("aria-label", "Zavrieť notifikáciu");
    closeBtn.addEventListener("click", () => dismiss());
    el.appendChild(closeBtn);

    container.appendChild(el);

    let timer = null;
    let dismissed = false;

    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      if (timer) { clearTimeout(timer); timer = null; }
      el.classList.add("kr-toast--leaving");
      /* Remove after animation. Use setTimeout as a safety net in case
         animationend does not fire (e.g. display:none removal). */
      const cleanup = () => el.remove();
      el.addEventListener("animationend", cleanup, { once: true });
      window.setTimeout(cleanup, 500);
    }

    if (duration > 0) {
      timer = window.setTimeout(dismiss, duration);
    }

    return { dismiss };
  }

  /* ==========================================================================
     Confirm — Promise-based confirmation dialog
     Built dynamically on top of Modal + native <dialog>.
     ========================================================================== */

  /**
   * Show a confirmation dialog.
   * @param {string|object} messageOrOptions  Message string, or options object.
   * @param {string} [messageOrOptions.title='Potvrdenie']
   * @param {string} [messageOrOptions.message]
   * @param {string} [messageOrOptions.confirmLabel='OK']
   * @param {string} [messageOrOptions.cancelLabel='Zrušiť']
   * @param {'primary'|'danger'} [messageOrOptions.variant='primary']
   * @returns {Promise<boolean>}  true if confirmed, false if cancelled.
   */
  function confirm(messageOrOptions) {
    const opts =
      typeof messageOrOptions === "string"
        ? { message: messageOrOptions }
        : (messageOrOptions || {});

    const title = opts.title || "Potvrdenie";
    const message = opts.message || "";
    const confirmLabel = opts.confirmLabel || "OK";
    const cancelLabel = opts.cancelLabel || "Zrušiť";
    const variant = opts.variant || "primary";

    return new Promise((resolve) => {
      const dialog = DOC.createElement("dialog");
      dialog.className = "kr-modal kr-modal--sm kr-modal--confirm";

      const header = DOC.createElement("header");
      header.className = "kr-modal__header";
      const titleEl = DOC.createElement("h3");
      titleEl.className = "kr-modal__title";
      titleEl.textContent = title;
      header.appendChild(titleEl);

      const body = DOC.createElement("div");
      body.className = "kr-modal__body";
      body.textContent = message;

      const footer = DOC.createElement("footer");
      footer.className = "kr-modal__footer";

      const cancelBtn = DOC.createElement("button");
      cancelBtn.type = "button";
      cancelBtn.className = "kr-btn kr-btn--ghost";
      cancelBtn.textContent = cancelLabel;

      const confirmBtn = DOC.createElement("button");
      confirmBtn.type = "button";
      confirmBtn.className = "kr-btn kr-btn--" + variant;
      confirmBtn.textContent = confirmLabel;

      footer.appendChild(cancelBtn);
      footer.appendChild(confirmBtn);

      dialog.appendChild(header);
      dialog.appendChild(body);
      dialog.appendChild(footer);
      DOC.body.appendChild(dialog);

      let settled = false;

      function finish(result) {
        if (settled) return;
        settled = true;
        Modal.close(dialog);
        /* Remove from DOM after close transition */
        window.setTimeout(() => dialog.remove(), 400);
        resolve(result);
      }

      cancelBtn.addEventListener("click", () => finish(false));
      confirmBtn.addEventListener("click", () => finish(true));
      /* ESC key fires the 'cancel' event on native dialogs */
      dialog.addEventListener("cancel", (e) => {
        e.preventDefault();
        finish(false);
      });
      /* Safety net: any close path we did not explicitly handle (e.g. the
         document-level backdrop-click handler calling Modal.close, or a
         programmatic .close()) must still settle the Promise. Without this,
         clicking the backdrop would close the dialog but leave the Promise
         pending and the DOM node leaked. */
      dialog.addEventListener("close", () => finish(false));

      Modal.open(dialog);
      /* Focus the confirm button for keyboard users */
      window.requestAnimationFrame(() => confirmBtn.focus());
    });
  }

  /* ==========================================================================
     Sidebar — collapsible app shell sidebar
     Toggles between expanded/collapsed on desktop, open/closed on mobile.
     Desktop state is persisted in localStorage.
     ========================================================================== */

  const SIDEBAR_STORAGE_KEY = "kr-sidebar";
  const SIDEBAR_MOBILE_QUERY = "(max-width: 47.99em)";

  function isMobileViewport() {
    return window.matchMedia(SIDEBAR_MOBILE_QUERY).matches;
  }

  const Sidebar = {
    /**
     * Toggle the nearest .kr-app sidebar.
     * @param {string|Element} [target]  Optional .kr-app reference. Defaults to first on page.
     */
    toggle(target) {
      const app = resolveElement(target) || DOC.querySelector(".kr-app");
      if (!app) return;
      if (isMobileViewport()) {
        app.dataset.sidebar = app.dataset.sidebar === "open" ? "closed" : "open";
      } else {
        const next = app.dataset.sidebar === "collapsed" ? "expanded" : "collapsed";
        app.dataset.sidebar = next;
        try { localStorage.setItem(SIDEBAR_STORAGE_KEY, next); } catch (_) {}
      }
      app.dispatchEvent(new CustomEvent("kr:sidebar:toggle", {
        bubbles: true,
        detail: { state: app.dataset.sidebar }
      }));
      syncScrollLock();
    },

    expand(target) {
      const app = resolveElement(target) || DOC.querySelector(".kr-app");
      if (!app) return;
      app.dataset.sidebar = isMobileViewport() ? "open" : "expanded";
      try { localStorage.setItem(SIDEBAR_STORAGE_KEY, "expanded"); } catch (_) {}
      syncScrollLock();
    },

    collapse(target) {
      const app = resolveElement(target) || DOC.querySelector(".kr-app");
      if (!app) return;
      app.dataset.sidebar = isMobileViewport() ? "closed" : "collapsed";
      try { localStorage.setItem(SIDEBAR_STORAGE_KEY, "collapsed"); } catch (_) {}
      syncScrollLock();
    },
  };

  /* -- Event delegation: toggle button ------------------------------------ */
  DOC.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-kr-sidebar-toggle]");
    if (!btn) return;
    event.preventDefault();
    const app = btn.closest(".kr-app");
    Sidebar.toggle(app);
  });

  /* -- Mobile: click outside sidebar (on backdrop) closes the drawer ----- */
  DOC.addEventListener("click", (event) => {
    if (!isMobileViewport()) return;
    const app = DOC.querySelector(".kr-app[data-sidebar='open']");
    if (!app) return;
    const sidebar = app.querySelector(".kr-app__sidebar");
    /* Ignore clicks inside sidebar or on the toggle button itself */
    if (sidebar && (sidebar.contains(event.target) || event.target.closest("[data-kr-sidebar-toggle]"))) {
      return;
    }
    app.dataset.sidebar = "closed";
    syncScrollLock();
  });

  /* -- ESC closes the drawer on mobile ------------------------------------ */
  DOC.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (!isMobileViewport()) return;
    const app = DOC.querySelector(".kr-app[data-sidebar='open']");
    if (!app) return;
    app.dataset.sidebar = "closed";
    syncScrollLock();
  });

  /* -- Close button for the mobile full-screen drawer --------------------
     On mobile the sidebar takes over the entire viewport, so the user needs
     a visible way to close it. We inject a button with `data-kr-sidebar-toggle`
     into .kr-sidebar__brand — the existing toggle click handler then fires
     and switches the drawer to `closed`. The button is removed again when
     the viewport crosses back to desktop so it does not linger as a phantom
     extra control in the expanded/collapsed sidebar. */

  function injectSidebarClose(app) {
    const brand = app.querySelector(".kr-app__sidebar .kr-sidebar__brand");
    if (!brand) return;
    if (brand.querySelector(":scope > .kr-sidebar__close")) return;

    const close = DOC.createElement("button");
    close.type = "button";
    close.className = "kr-sidebar__close";
    close.setAttribute("aria-label", "Zavrieť menu");
    close.setAttribute("data-kr-sidebar-toggle", "");
    brand.appendChild(close);
  }

  function removeSidebarClose(app) {
    const close = app.querySelector(".kr-app__sidebar .kr-sidebar__brand > .kr-sidebar__close");
    if (close) close.remove();
  }

  /* -- Initialise sidebar state on DOM ready --------------------------------
     NOTE: Kairo supports a single .kr-app per page. Multiple app shells
     are not supported — querySelector returns the first match only. */
  function initSidebar() {
    const app = DOC.querySelector(".kr-app");
    if (!app) return;
    if (isMobileViewport()) {
      /* Always start closed on mobile; mobile state is not persisted */
      app.dataset.sidebar = "closed";
      injectSidebarClose(app);
      return;
    }
    let saved = null;
    try { saved = localStorage.getItem(SIDEBAR_STORAGE_KEY); } catch (_) {}
    app.dataset.sidebar = (saved === "collapsed" || saved === "expanded") ? saved : "expanded";
    removeSidebarClose(app);
  }

  if (DOC.readyState === "loading") {
    DOC.addEventListener("DOMContentLoaded", initSidebar);
  } else {
    initSidebar();
  }

  /* -- Re-evaluate state when crossing the mobile/desktop boundary -------- */
  const mobileMQ = window.matchMedia(SIDEBAR_MOBILE_QUERY);
  mobileMQ.addEventListener("change", () => {
    const app = DOC.querySelector(".kr-app");
    if (!app) return;
    if (isMobileViewport()) {
      app.dataset.sidebar = "closed";
      injectSidebarClose(app);
    } else {
      let saved = null;
      try { saved = localStorage.getItem(SIDEBAR_STORAGE_KEY); } catch (_) {}
      app.dataset.sidebar = saved === "collapsed" ? "collapsed" : "expanded";
      removeSidebarClose(app);
    }
    syncScrollLock();
  });

  /* ==========================================================================
     Menu — <details>-based dropdown auto-close
     The element handles open/close natively. We only add:
       - click outside → close
       - ESC          → close + restore focus to trigger
       - item click   → close (so menus work like native menus)
     ========================================================================== */

  DOC.addEventListener("click", (event) => {
    /* Close when a menu item is activated */
    const item = event.target.closest(".kr-menu__item");
    if (item) {
      const menu = item.closest("details.kr-menu");
      if (menu) menu.open = false;
      return;
    }

    /* Close any open menu when clicking outside of it */
    const openMenus = DOC.querySelectorAll("details.kr-menu[open]");
    openMenus.forEach((menu) => {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });

  DOC.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const openMenus = DOC.querySelectorAll("details.kr-menu[open]");
    if (!openMenus.length) return;
    openMenus.forEach((menu) => {
      menu.open = false;
      /* Restore keyboard focus to the trigger */
      const trigger = menu.querySelector(":scope > summary");
      if (trigger) trigger.focus();
    });
  });

  /* ------------------------------------------------------------------------
     Mobile sheet header for kr-menu
     Under the tablet breakpoint, kr-menu becomes a full-screen overlay
     (see menu.css). On open we inject a header node into .kr-menu__list
     with a title (from the trigger's aria-label or text) and a close X
     button, giving the sheet clear affordances. On close we remove it.
     Desktop is unaffected — the header has `display: none` via media query.
     ------------------------------------------------------------------------ */

  const MENU_MOBILE_QUERY = "(max-width: 47.9375em)";

  function isMenuMobile() {
    return window.matchMedia(MENU_MOBILE_QUERY).matches;
  }

  function getMenuSheetTitle(menu) {
    const trigger = menu.querySelector(":scope > summary");
    if (!trigger) return "";
    /* Explicit override — used by components like kr-select-menu where the
       summary's visible text is a dynamic value and the real title lives
       elsewhere. */
    const explicit = trigger.dataset.krSheetTitle;
    if (explicit) return explicit.trim();
    const label = trigger.getAttribute("aria-label");
    if (label) return label.trim();
    return (trigger.textContent || "").trim();
  }

  function injectMenuSheetHeader(menu) {
    const list = menu.querySelector(":scope > .kr-menu__list");
    if (!list) return;
    if (list.querySelector(":scope > .kr-menu__sheet-header")) return;

    const header = DOC.createElement("div");
    header.className = "kr-menu__sheet-header";

    const titleText = getMenuSheetTitle(menu);
    if (titleText) {
      const title = DOC.createElement("span");
      title.className = "kr-menu__sheet-title";
      title.textContent = titleText;
      header.appendChild(title);
    }

    const close = DOC.createElement("button");
    close.type = "button";
    close.className = "kr-menu__sheet-close";
    close.setAttribute("aria-label", "Zavrieť");
    header.appendChild(close);

    list.insertBefore(header, list.firstChild);
  }

  function removeMenuSheetHeader(menu) {
    const list = menu.querySelector(":scope > .kr-menu__list");
    if (!list) return;
    const header = list.querySelector(":scope > .kr-menu__sheet-header");
    if (header) header.remove();
  }

  /* <details> dispatches `toggle` AFTER .open has flipped, so we can read
     the new state directly. Capture phase ensures we run before any user
     listener that might stop propagation. */
  DOC.addEventListener("toggle", (event) => {
    const menu = event.target;
    if (!(menu instanceof Element)) return;
    if (!menu.matches || !menu.matches("details.kr-menu")) return;
    if (menu.open && isMenuMobile()) {
      injectMenuSheetHeader(menu);
    } else {
      removeMenuSheetHeader(menu);
    }
    syncScrollLock();
  }, true);

  /* Dedicated click handler for the sheet close button. The outside-click
     handler defined above treats this click as "inside" (the close button
     lives in the list), so it will not interfere. */
  DOC.addEventListener("click", (event) => {
    const close = event.target.closest(".kr-menu__sheet-close");
    if (!close) return;
    const menu = close.closest("details.kr-menu");
    if (menu) menu.open = false;
  });

  /* When the viewport crosses the mobile boundary with a menu still open,
     sync the header so the UI matches the new layout. */
  window.matchMedia(MENU_MOBILE_QUERY).addEventListener("change", () => {
    DOC.querySelectorAll("details.kr-menu[open]").forEach((menu) => {
      if (isMenuMobile()) {
        injectMenuSheetHeader(menu);
      } else {
        removeMenuSheetHeader(menu);
      }
    });
  });

  /* ==========================================================================
     Tabs — ARIA tablist with keyboard navigation
     Selects on click, supports ArrowLeft/Right/Home/End + activation.
     Panels are toggled via the `hidden` attribute (so CSS controls the
     enter animation via :not([hidden])).

     Contract:
       - .kr-tabs__list[role="tablist"] wraps .kr-tabs__tab[role="tab"] buttons
       - each tab has aria-controls pointing to a .kr-tabs__panel[role="tabpanel"]
       - only one tab is [aria-selected="true"], the rest are "false"
       - roving tabindex: selected tab has 0, others -1
     ========================================================================== */

  /**
   * Activate a tab and reveal its panel.
   * @param {HTMLElement} tab — the tab (role="tab") to select
   * @param {Object} [options]
   * @param {boolean} [options.silent=false] — when true, do NOT dispatch
   *   the kr:tab:change event. Used by initTabs() to sync initial state
   *   without firing a change notification for markup that was already
   *   in its correct state.
   */
  const selectTab = (tab, options) => {
    const silent = !!(options && options.silent);

    const list = tab.closest(".kr-tabs__list");
    if (!list) return;

    const tabs = list.querySelectorAll(':scope > [role="tab"]');
    tabs.forEach((t) => {
      const selected = t === tab;
      t.setAttribute("aria-selected", selected ? "true" : "false");
      t.setAttribute("tabindex", selected ? "0" : "-1");
    });

    /* Reveal the controlled panel, hide siblings */
    const wrap = list.closest(".kr-tabs");
    if (!wrap) return;
    const panels = wrap.querySelectorAll(':scope > [role="tabpanel"]');
    const targetId = tab.getAttribute("aria-controls");
    panels.forEach((panel) => {
      panel.toggleAttribute("hidden", panel.id !== targetId);
    });

    /* Sync the mobile dropdown proxy (kr-menu built by initTabs) */
    const mobileNav = wrap.querySelector(":scope > .kr-tabs__mobile-nav");
    if (mobileNav) {
      const trigger = mobileNav.querySelector(":scope > summary");
      if (trigger) trigger.textContent = tab.textContent.trim();
      mobileNav.querySelectorAll(".kr-menu__item[data-kr-tab-target]").forEach((item) => {
        item.classList.toggle("kr-menu__item--active", item.dataset.krTabTarget === tab.id);
      });
    }

    if (!silent) {
      tab.dispatchEvent(new CustomEvent("kr:tab:change", {
        bubbles: true,
        detail: { tabId: tab.id, panelId: targetId },
      }));
    }
  };

  /* On narrow viewports we replace the tablist with a <details>-based
     dropdown (reusing the kr-menu component). Native <select> pickers
     on Safari/iOS can render in unpredictable positions, so we build
     our own. CSS handles the viewport-based visibility swap; the proxy
     can always live in the DOM. */
  const initTabs = () => {
    DOC.querySelectorAll(".kr-tabs").forEach((wrap) => {
      if (wrap.querySelector(":scope > .kr-tabs__mobile-nav")) return;

      const list = wrap.querySelector(":scope > .kr-tabs__list");
      if (!list) return;

      const tabs = list.querySelectorAll(':scope > [role="tab"]');
      if (tabs.length < 2) return;

      const active = list.querySelector(':scope > [aria-selected="true"]') || tabs[0];

      const nav = DOC.createElement("details");
      nav.className = "kr-menu kr-tabs__mobile-nav";

      const trigger = DOC.createElement("summary");
      trigger.className = "kr-menu__trigger";
      trigger.setAttribute("aria-label", list.getAttribute("aria-label") || "Sekcie");
      trigger.textContent = active.textContent.trim();
      nav.appendChild(trigger);

      const menu = DOC.createElement("div");
      menu.className = "kr-menu__list";
      menu.setAttribute("role", "menu");

      tabs.forEach((tab) => {
        const item = DOC.createElement("button");
        item.type = "button";
        item.className = "kr-menu__item";
        if (tab === active) item.classList.add("kr-menu__item--active");
        if (tab.hasAttribute("disabled") || tab.getAttribute("aria-disabled") === "true") {
          item.setAttribute("aria-disabled", "true");
        }
        item.textContent = tab.textContent.trim();
        item.dataset.krTabTarget = tab.id;
        menu.appendChild(item);
      });
      nav.appendChild(menu);

      wrap.insertBefore(nav, list);

      /* Force-sync the initial state: ARIA attributes, panel visibility and
         the mobile proxy. Runs silently so pages that already had a correct
         initial state do not receive a phantom kr:tab:change event. */
      selectTab(active, { silent: true });
    });
  };

  /* Delegate clicks on mobile proxy items → activate the matching tab.
     Runs before/alongside the existing menu click handler which closes
     the dropdown. */
  DOC.addEventListener("click", (event) => {
    const item = event.target.closest(".kr-menu__item[data-kr-tab-target]");
    if (!item) return;
    if (item.getAttribute("aria-disabled") === "true") return;
    const target = DOC.getElementById(item.dataset.krTabTarget);
    if (target) selectTab(target);
  });

  if (DOC.readyState === "loading") {
    DOC.addEventListener("DOMContentLoaded", initTabs);
  } else {
    initTabs();
  }

  DOC.addEventListener("click", (event) => {
    const tab = event.target.closest('.kr-tabs__list > [role="tab"]');
    if (!tab) return;
    if (tab.hasAttribute("disabled") || tab.getAttribute("aria-disabled") === "true") return;
    selectTab(tab);
    tab.focus();
  });

  DOC.addEventListener("keydown", (event) => {
    const tab = event.target.closest('.kr-tabs__list > [role="tab"]');
    if (!tab) return;

    const list = tab.parentElement;
    const tabs = Array.from(list.querySelectorAll(':scope > [role="tab"]:not([disabled]):not([aria-disabled="true"])'));
    const index = tabs.indexOf(tab);
    if (index < 0) return;

    let next = null;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = tabs[(index + 1) % tabs.length];
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = tabs[(index - 1 + tabs.length) % tabs.length];
        break;
      case "Home":
        next = tabs[0];
        break;
      case "End":
        next = tabs[tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    selectTab(next);
    next.focus();
  });

  /* ==========================================================================
     Select — custom dropdown over <details>/<summary>
     Upgrades <select data-kr-select> in place. The real <select> stays as
     the form source of truth; open/close/ESC/outside-click/mobile sheet
     takeover all come from the kr-menu infrastructure above.
     ========================================================================== */

  function findSelectLabelText(selectEl) {
    let target = null;
    const enclosing = selectEl.closest("label");
    if (enclosing) {
      target = enclosing.querySelector(".kr-field__label") || enclosing;
    } else if (selectEl.id) {
      try {
        const escaped = (window.CSS && typeof window.CSS.escape === "function")
          ? window.CSS.escape(selectEl.id)
          : selectEl.id;
        target = DOC.querySelector('label[for="' + escaped + '"]');
      } catch (_err) { /* ignore invalid id */ }
    }
    if (!target) return selectEl.getAttribute("aria-label") || "";
    /* Strip the required marker so it doesn't leak into the accessible
       name or the mobile sheet title. */
    const clone = target.cloneNode(true);
    clone.querySelectorAll(".kr-field__required").forEach((el) => el.remove());
    return (clone.textContent || "").trim();
  }

  function syncSelectState(details) {
    const select = details.querySelector(":scope > select");
    const valueSpan = details.querySelector(".kr-select-menu__value");
    if (!select || !valueSpan) return;

    const current = select.value;
    const selectedOpt = select.options[select.selectedIndex] || null;
    const isPlaceholder = !selectedOpt || selectedOpt.value === "";

    valueSpan.textContent = selectedOpt ? selectedOpt.textContent : "";
    valueSpan.classList.toggle("kr-select-menu__value--placeholder", isPlaceholder);

    details.querySelectorAll(".kr-select-menu__option").forEach((opt) => {
      opt.setAttribute("aria-selected", opt.dataset.value === current ? "true" : "false");
    });

    /* Sync disabled state — keep wrapper in sync with the source <select> */
    if (select.disabled) {
      details.dataset.disabled = "";
    } else {
      delete details.dataset.disabled;
    }
  }

  /** Commit a user-driven change: sync visible UI and fire the bubbling
      kr:select:change event. Used by the click handler, the change
      delegation listener and the reset listener. */
  function commitSelectChange(details) {
    const select = details.querySelector(":scope > select");
    if (!select) return;
    syncSelectState(details);
    details.dispatchEvent(new CustomEvent("kr:select:change", {
      bubbles: true,
      detail: { value: select.value, select },
    }));
  }

  function buildSelectOption(opt) {
    const item = DOC.createElement("button");
    item.type = "button";
    item.className = "kr-menu__item kr-select-menu__option";
    item.setAttribute("role", "option");
    item.dataset.value = opt.value;
    if (opt.disabled) item.setAttribute("aria-disabled", "true");
    if (opt.hidden) item.hidden = true;
    item.textContent = opt.textContent;
    return item;
  }

  function upgradeSelect(selectEl) {
    if (selectEl.dataset.krSelectReady === "1") return;

    const labelText = findSelectLabelText(selectEl);

    const details = DOC.createElement("details");
    details.className = "kr-menu kr-select-menu";
    if (selectEl.classList.contains("kr-select--sm")) details.classList.add("kr-select-menu--sm");
    if (selectEl.classList.contains("kr-select--lg")) details.classList.add("kr-select-menu--lg");
    if (selectEl.disabled) details.dataset.disabled = "true";

    const summary = DOC.createElement("summary");
    summary.className = "kr-menu__trigger kr-select-menu__trigger";
    summary.setAttribute("aria-haspopup", "listbox");

    /* Visually-hidden label so the summary's accessible name reads
       "{label} {value}" without duplicating the label visually. */
    if (labelText) {
      summary.dataset.krSheetTitle = labelText;
      const srLabel = DOC.createElement("span");
      srLabel.className = "kr-sr-only";
      srLabel.textContent = labelText + " ";
      summary.appendChild(srLabel);
    }

    const valueSpan = DOC.createElement("span");
    valueSpan.className = "kr-select-menu__value";
    summary.appendChild(valueSpan);

    details.appendChild(summary);

    const list = DOC.createElement("div");
    list.className = "kr-menu__list";
    list.setAttribute("role", "listbox");
    if (labelText) list.setAttribute("aria-label", labelText);

    /* Walk direct children so <optgroup> boundaries are preserved.
       Groups are rendered flat (label as a sibling div, options as
       direct children of the list) so existing menu.css child-combinator
       rules — including mobile tap-padding — keep working unchanged. */
    Array.from(selectEl.children).forEach((node) => {
      if (node.tagName === "OPTGROUP") {
        if (node.label) {
          const head = DOC.createElement("div");
          head.className = "kr-select-menu__group-label";
          head.setAttribute("aria-hidden", "true");
          head.textContent = node.label;
          list.appendChild(head);
        }
        Array.from(node.children).forEach((opt) => {
          if (opt.tagName === "OPTION") list.appendChild(buildSelectOption(opt));
        });
      } else if (node.tagName === "OPTION") {
        list.appendChild(buildSelectOption(node));
      }
    });
    details.appendChild(list);

    /* Move the real select inside the wrapper as the form source of truth.
       CSS keeps it sized and positioned so the browser's native constraint
       validation tooltip lands on the trigger; aria-hidden + tabindex=-1
       take it out of the AT and tab flow. */
    const parent = selectEl.parentNode;
    parent.insertBefore(details, selectEl);
    details.appendChild(selectEl);
    selectEl.classList.remove("kr-select", "kr-select--sm", "kr-select--lg");
    selectEl.setAttribute("tabindex", "-1");
    selectEl.setAttribute("aria-hidden", "true");
    selectEl.dataset.krSelectReady = "1";

    syncSelectState(details);
  }

  const initSelect = () => {
    DOC.querySelectorAll("select[data-kr-select]").forEach(upgradeSelect);
  };

  if (DOC.readyState === "loading") {
    DOC.addEventListener("DOMContentLoaded", initSelect);
  } else {
    initSelect();
  }

  /* ==========================================================================
     Field a11y wiring — auto aria-describedby for help/error text
     A `<label class="kr-field">` wraps an input and optional help/error
     spans. The nested input is already labelled by the <label>, but help
     and error text need `aria-describedby` so screen readers announce
     them when the input is focused. We generate stable ids and wire them.
     ========================================================================== */

  let fieldDescribeSeq = 0;
  const FIELD_CONTROL_SELECTOR =
    "input:not([type=hidden]), textarea, select, " +
    "summary.kr-select-menu__trigger";

  function wireField(field) {
    if (field.dataset.krFieldWired === "1") return;

    const control = field.querySelector(FIELD_CONTROL_SELECTOR);
    if (!control) return;

    const describers = field.querySelectorAll(
      ":scope > .kr-field__help, :scope > .kr-field__error"
    );
    if (!describers.length) return;

    const ids = [];
    describers.forEach((el) => {
      if (!el.id) {
        fieldDescribeSeq += 1;
        el.id = "kr-field-desc-" + fieldDescribeSeq;
      }
      ids.push(el.id);
    });

    /* Preserve any pre-existing aria-describedby set by the author */
    const existing = (control.getAttribute("aria-describedby") || "")
      .split(/\s+/)
      .filter(Boolean);
    ids.forEach((id) => {
      if (!existing.includes(id)) existing.push(id);
    });
    control.setAttribute("aria-describedby", existing.join(" "));
    field.dataset.krFieldWired = "1";
  }

  const initFields = () => {
    DOC.querySelectorAll(".kr-field").forEach(wireField);
  };

  /* ==========================================================================
     Slider — paint the filled portion of a range track on WebKit/Blink
     by writing a --_progress CSS var on the <input>. Firefox handles this
     natively via ::-moz-range-progress, so this is purely a parity shim.
     ========================================================================== */
  function updateSliderProgress(slider) {
    const min = Number(slider.min) || 0;
    const max = Number(slider.max) || 100;
    const span = max - min;
    const pct = span > 0 ? ((slider.valueAsNumber - min) / span) * 100 : 0;
    /* Round to two decimals so identical positions reuse the same string
       and the browser's style cache can hit on repeated drags. */
    slider.style.setProperty("--_progress", pct.toFixed(2) + "%");
  }

  const initSliders = () => {
    DOC.querySelectorAll("input.kr-slider").forEach(updateSliderProgress);
  };

  /* Delegate input events so dynamically-added sliders don't need re-init.
     Pre-filter by tagName + type before touching the class list to keep
     this cheap for the many non-range inputs on the page. */
  DOC.addEventListener("input", (event) => {
    const el = event.target;
    if (
      el &&
      el.tagName === "INPUT" &&
      el.type === "range" &&
      el.classList.contains("kr-slider")
    ) {
      updateSliderProgress(el);
    }
  });

  const initFieldsAndSliders = () => {
    initFields();
    initSliders();
  };

  if (DOC.readyState === "loading") {
    DOC.addEventListener("DOMContentLoaded", initFieldsAndSliders);
  } else {
    initFieldsAndSliders();
  }

  DOC.addEventListener("click", (event) => {
    const option = event.target.closest(".kr-select-menu__option");
    if (!option) return;
    if (option.getAttribute("aria-disabled") === "true") return;

    const details = option.closest("details.kr-select-menu");
    if (!details) return;
    const select = details.querySelector(":scope > select");
    if (!select) return;

    const newValue = option.dataset.value;
    /* No-op gate — clicking the already-selected option should not fire
       change events or re-run the sync/dispatch pipeline. */
    if (select.value === newValue) return;

    select.value = newValue;
    select.dispatchEvent(new Event("input",  { bubbles: true }));
    select.dispatchEvent(new Event("change", { bubbles: true }));
    /* Change listener below takes over and runs commitSelectChange. */
  });

  /* Any change on a hidden upgraded <select> — click-driven, JS dispatched,
     or bubbled from a reset — is the single commit path. */
  DOC.addEventListener("change", (event) => {
    const select = event.target;
    if (!select || select.tagName !== "SELECT") return;
    const details = select.parentElement;
    if (!details || !details.classList || !details.classList.contains("kr-select-menu")) return;
    commitSelectChange(details);
  });

  /* Form reset — the HTML reset algorithm restores each control's default
     value but does NOT fire change on them, so we listen for reset on the
     form and re-sync on the next frame (reset event fires before the
     algorithm has fully applied in some code paths). */
  DOC.addEventListener("reset", (event) => {
    const form = event.target;
    if (!form || form.tagName !== "FORM") return;
    window.requestAnimationFrame(() => {
      form.querySelectorAll("details.kr-select-menu > select").forEach((select) => {
        commitSelectChange(select.parentElement);
      });
    });
  });

  /* Constraint validation — when the hidden select fails validation on
     submit, the browser tries to focus it. Redirect focus to the visible
     summary so the user actually sees the error ring. invalid events
     don't bubble, hence capture. */
  DOC.addEventListener("invalid", (event) => {
    const select = event.target;
    if (!select || select.tagName !== "SELECT") return;
    const details = select.parentElement;
    if (!details || !details.classList || !details.classList.contains("kr-select-menu")) return;
    const summary = details.querySelector(":scope > summary");
    if (summary) {
      window.requestAnimationFrame(() => summary.focus());
    }
  }, true);

  /* On open — scroll the currently-selected option into view so long
     lists don't start at the top. toggle events don't bubble, so use
     capture phase. */
  DOC.addEventListener("toggle", (event) => {
    const details = event.target;
    if (!details || !details.classList || !details.classList.contains("kr-select-menu")) return;
    if (!details.open) return;
    const selected = details.querySelector('.kr-select-menu__option[aria-selected="true"]');
    if (selected) selected.scrollIntoView({ block: "nearest" });
  }, true);

  const SELECT_NAV_KEYS = new Set([
    "ArrowDown", "ArrowUp", "Home", "End",
  ]);

  /* Typeahead state — per-instance via WeakMap so switching between
     two selects doesn't carry over the prefix from the first one. */
  const typeaheadState = new WeakMap();

  function typeaheadJump(details, char) {
    let state = typeaheadState.get(details);
    if (!state) {
      state = { buffer: "", timer: 0 };
      typeaheadState.set(details, state);
    }
    if (state.timer) clearTimeout(state.timer);
    const lower = char.toLowerCase();
    /* Repeating the same single char cycles through options starting
       with that letter instead of accumulating into "bb". Multi-char
       prefixes like "br" still work because the new char differs. */
    if (state.buffer !== lower) {
      state.buffer += lower;
    }
    state.timer = setTimeout(() => { state.buffer = ""; state.timer = 0; }, 500);

    const options = Array.from(details.querySelectorAll(
      '.kr-select-menu__option:not([aria-disabled="true"]):not([hidden])'
    ));
    if (!options.length) return;

    /* Start searching after the currently-focused option so repeat-letter
       typing cycles through matches (native select behaviour). */
    const activeIdx = options.indexOf(DOC.activeElement);
    const startAt = activeIdx >= 0 ? activeIdx + 1 : 0;
    const rotated = options.slice(startAt).concat(options.slice(0, startAt));
    const match = rotated.find((opt) => (opt.textContent || "").trim().toLowerCase().startsWith(state.buffer));
    if (match) match.focus();
  }

  DOC.addEventListener("keydown", (event) => {
    /* Early bailout before DOM traversal — this handler runs on every
       keystroke, so check the key first. */
    const key = event.key;
    const isNavKey = SELECT_NAV_KEYS.has(key);
    const isTypeaheadKey =
      key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (!isNavKey && !isTypeaheadKey) return;

    const details = event.target.closest("details.kr-select-menu");
    if (!details) return;

    if (isNavKey && event.target.matches("summary.kr-select-menu__trigger")) {
      if (key === "ArrowDown" || key === "ArrowUp") {
        event.preventDefault();
        if (!details.open) details.open = true;
        const selected = details.querySelector('.kr-select-menu__option[aria-selected="true"]');
        const first = details.querySelector(
          '.kr-select-menu__option:not([aria-disabled="true"]):not([hidden])'
        );
        const target = selected || first;
        if (target) target.focus();
      }
      return;
    }

    if (isTypeaheadKey) {
      if (!details.open) return;
      typeaheadJump(details, key);
      event.preventDefault();
      return;
    }

    const option = event.target.closest(".kr-select-menu__option");
    if (!option) return;
    const options = Array.from(details.querySelectorAll(
      '.kr-select-menu__option:not([aria-disabled="true"]):not([hidden])'
    ));
    const idx = options.indexOf(option);
    if (idx < 0) return;

    let next = null;
    switch (key) {
      case "ArrowDown": next = options[(idx + 1) % options.length]; break;
      case "ArrowUp":   next = options[(idx - 1 + options.length) % options.length]; break;
      case "Home":      next = options[0]; break;
      case "End":       next = options[options.length - 1]; break;
    }
    event.preventDefault();
    if (next) next.focus();
  });

  /* ==========================================================================
     Popover — fallback positioning
     For browsers that implement the native Popover API but not CSS Anchor
     Positioning (e.g. Safari/Firefox without position-area), we compute the
     popover's coordinates from the invoker's bounding rect and write them
     to inline top/left. Modern browsers skip this path entirely.

     The popover's invoker is the element that opened it — either found via
     the native `source` property on the `toggle` event, or located by
     scanning for a matching `popovertarget` attribute.
     ========================================================================== */

  const HAS_ANCHOR_POSITIONING =
    typeof window.CSS !== "undefined" &&
    typeof window.CSS.supports === "function" &&
    window.CSS.supports("position-area", "top");

  /* Remember the real invoker for each open popover.
     `querySelector([popovertarget="..."])` only ever returns the FIRST
     element that targets a given popover, so when a page has multiple
     triggers for the same popover (e.g. a toolbar menu repeated per row)
     the fallback would position against the wrong one. The `toggle`
     event carries the true source element; we capture it there and fall
     back to querySelector only when the browser doesn't expose it
     (Firefox/Safari don't surface ToggleEvent.source yet) or when the
     popover was opened programmatically via `.showPopover()` without
     an invoker. */
  const popoverInvokers = new WeakMap();

  /** Find the element that invoked a popover. */
  function findPopoverInvoker(popover) {
    if (!popover) return null;
    const cached = popoverInvokers.get(popover);
    if (cached && cached.isConnected) return cached;
    if (!popover.id) return null;
    /* Escape ID for attribute selector — IDs may contain quotes / brackets */
    try {
      const escaped = (window.CSS && typeof window.CSS.escape === "function")
        ? window.CSS.escape(popover.id)
        : popover.id.replace(/"/g, '\\"');
      return DOC.querySelector('[popovertarget="' + escaped + '"]');
    } catch (_err) {
      return null;
    }
  }

  /** Preferred side for a popover based on its modifier class. */
  function getPopoverSide(popover) {
    if (popover.classList.contains("kr-popover--bottom")) return "bottom";
    if (popover.classList.contains("kr-popover--start")) return "start";
    if (popover.classList.contains("kr-popover--end")) return "end";
    return "top";
  }

  /**
   * Position a popover relative to its invoker (JS fallback).
   * Uses fixed positioning — coordinates are in the viewport frame.
   * Attempts the preferred side, then flips to the opposite axis if the
   * popover would overflow the viewport.
   */
  function positionPopover(popover) {
    const invoker = findPopoverInvoker(popover);
    if (!invoker) return;

    /* Reset any prior inline positioning so the measurement is accurate */
    popover.style.top = "";
    popover.style.left = "";

    const trigger = invoker.getBoundingClientRect();
    const rect = popover.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 8; /* matches --kr-space-2 */

    let side = getPopoverSide(popover);

    /* Flip vertically if not enough room on the preferred side */
    if (side === "top" && trigger.top - rect.height - gap < 0) side = "bottom";
    else if (side === "bottom" && trigger.bottom + rect.height + gap > vh) side = "top";
    /* Flip horizontally */
    if (side === "start" && trigger.left - rect.width - gap < 0) side = "end";
    else if (side === "end" && trigger.right + rect.width + gap > vw) side = "start";

    let top = 0;
    let left = 0;
    switch (side) {
      case "top":
        top = trigger.top - rect.height - gap;
        left = trigger.left + trigger.width / 2 - rect.width / 2;
        break;
      case "bottom":
        top = trigger.bottom + gap;
        left = trigger.left + trigger.width / 2 - rect.width / 2;
        break;
      case "start":
        top = trigger.top + trigger.height / 2 - rect.height / 2;
        left = trigger.left - rect.width - gap;
        break;
      case "end":
        top = trigger.top + trigger.height / 2 - rect.height / 2;
        left = trigger.right + gap;
        break;
    }

    /* Clamp inside the viewport (with 8px margin) */
    const margin = 8;
    top = Math.max(margin, Math.min(top, vh - rect.height - margin));
    left = Math.max(margin, Math.min(left, vw - rect.width - margin));

    popover.style.top = top + "px";
    popover.style.left = left + "px";
  }

  /* Single capture-phase toggle listener handles both invoker caching and
     the non-anchor fallback positioning. Runs in every browser so the
     WeakMap stays populated for consumers like `Kairo.Popover.position(el)`. */
  DOC.addEventListener("toggle", (event) => {
    const popover = event.target;
    if (!popover.classList || !popover.classList.contains("kr-popover")) return;

    if (event.newState === "open") {
      /* ToggleEvent.source is the invoker (button with popovertarget).
         Chromium exposes it; Firefox/Safari may not, and programmatic
         `.showPopover()` has no source at all — in both cases
         `findPopoverInvoker` falls back to querySelector. */
      const source = event.source;
      if (source && source.nodeType === 1) {
        popoverInvokers.set(popover, source);
      }
      if (!HAS_ANCHOR_POSITIONING) {
        /* Wait for layout so the popover has its block size */
        window.requestAnimationFrame(() => positionPopover(popover));
      }
    } else {
      popoverInvokers.delete(popover);
    }
  }, true);

  if (!HAS_ANCHOR_POSITIONING) {
    /* Recompute on resize/scroll while any popover is open */
    const repositionOpenPopovers = () => {
      DOC.querySelectorAll(".kr-popover:popover-open").forEach(positionPopover);
    };
    window.addEventListener("resize", repositionOpenPopovers);
    window.addEventListener("scroll", repositionOpenPopovers, true);
  }

  /* ==========================================================================
     Command palette — Cmd-K / Ctrl-K search dialog.
     Opens via keyboard shortcut or `Kairo.Command.open(id)`. Arrow keys
     navigate, Enter selects, typing filters. Authors must supply an
     `onSelect(item)` callback via `data-kr-command-select` or override
     the default event.
     ========================================================================== */
  function initCommand(dialog) {
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog.hasAttribute("data-kr-command-ready")) return;
    dialog.setAttribute("data-kr-command-ready", "");
    const input = dialog.querySelector(".kr-command__input");
    const list = dialog.querySelector(".kr-command__list");
    if (!input || !list) return;

    const items = () =>
      [...list.querySelectorAll(".kr-command__item:not([hidden])")];

    function setActive(item) {
      items().forEach((i) => {
        i.classList.remove("kr-command__item--active");
        i.removeAttribute("aria-selected");
      });
      if (item) {
        item.classList.add("kr-command__item--active");
        item.setAttribute("aria-selected", "true");
        item.scrollIntoView({ block: "nearest" });
      }
    }

    function move(delta) {
      const all = items();
      if (!all.length) return;
      const cur = all.findIndex((i) =>
        i.classList.contains("kr-command__item--active")
      );
      const next = (cur + delta + all.length) % all.length;
      setActive(all[next]);
    }

    function select() {
      const active = list.querySelector(".kr-command__item--active");
      if (!active) return;
      const ev = new CustomEvent("kr-command-select", {
        detail: { item: active, value: active.dataset.value || active.textContent.trim() },
        bubbles: true,
      });
      dialog.dispatchEvent(ev);
      dialog.close();
    }

    /* Filter items by input value */
    input.addEventListener("input", () => {
      const q = input.value.trim().toLowerCase();
      let first = null;
      list.querySelectorAll(".kr-command__item").forEach((item) => {
        const text = item.textContent.toLowerCase();
        const match = !q || text.includes(q);
        item.hidden = !match;
        if (match && !first) first = item;
      });
      /* Hide empty group headers */
      list.querySelectorAll(".kr-command__group").forEach((g) => {
        let next = g.nextElementSibling;
        let hasVisible = false;
        while (next && !next.classList.contains("kr-command__group")) {
          if (!next.hidden) hasVisible = true;
          next = next.nextElementSibling;
        }
        g.hidden = !hasVisible;
      });
      setActive(first);
    });

    /* Keyboard navigation */
    input.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); move(1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); move(-1); }
      else if (e.key === "Enter") { e.preventDefault(); select(); }
    });

    /* Click selection */
    list.addEventListener("click", (e) => {
      const item = e.target.closest(".kr-command__item");
      if (item) { setActive(item); select(); }
    });

    /* Reset on open */
    dialog.addEventListener("kr-command-open", () => {
      input.value = "";
      list.querySelectorAll(".kr-command__item, .kr-command__group")
        .forEach((i) => (i.hidden = false));
      const all = items();
      setActive(all[0] || null);
      /* Schedule focus after the dialog opening paint */
      requestAnimationFrame(() => input.focus());
      syncScrollLock();
    });

    /* Sync scroll lock on close */
    dialog.addEventListener("close", () => { syncScrollLock(); });
  }

  /* Global shortcut — Cmd-K / Ctrl-K */
  DOC.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      const dialog = DOC.querySelector("dialog.kr-command");
      if (!dialog) return;
      e.preventDefault();
      if (dialog.open) { dialog.close(); return; }
      dialog.showModal();
      dialog.dispatchEvent(new Event("kr-command-open"));
    }
  });

  /* Auto-init on page load */
  const initAllCommands = () => {
    DOC.querySelectorAll("dialog.kr-command").forEach(initCommand);
  };
  if (DOC.readyState === "loading") {
    DOC.addEventListener("DOMContentLoaded", initAllCommands);
  } else {
    initAllCommands();
  }

  /* ==========================================================================
     Copy to clipboard — for .kr-code__copy buttons.
     Copies the adjacent <pre><code> text and briefly sets [data-copied].
     ========================================================================== */
  DOC.addEventListener("click", (e) => {
    const btn = e.target.closest(".kr-code__copy, [data-kr-copy]");
    if (!btn) return;
    const block = btn.closest(".kr-code");
    if (!block) return;
    const code = block.querySelector("pre code") || block.querySelector("pre");
    if (!code) return;
    navigator.clipboard.writeText(code.textContent).then(() => {
      btn.setAttribute("data-copied", "");
      setTimeout(() => btn.removeAttribute("data-copied"), 1500);
    });
  });

  /* ==========================================================================
     Export to global Kairo namespace
     ========================================================================== */

  const Kairo = window.Kairo || {};
  Kairo.Modal = Modal;
  Kairo.Drawer = Modal; /* alias — both names resolve to the same handler */
  Kairo.Sidebar = Sidebar;
  Kairo.theme = theme;
  Kairo.Tabs = { init: initTabs, select: selectTab };
  Kairo.Select = {
    init: initSelect,
    sync(target) {
      const el = resolveElement(target);
      if (!el) return false;
      const details = el.closest("details.kr-select-menu");
      if (!details) return false;
      syncSelectState(details);
      return true;
    },
  };
  Kairo.Field = { init: initFields };
  Kairo.Slider = { init: initSliders, update: updateSliderProgress };
  Kairo.Popover = { position: positionPopover };
  Kairo.Command = {
    init: initAllCommands,
    open(id) {
      const d = DOC.getElementById(id);
      if (d && d instanceof HTMLDialogElement && !d.open) {
        d.showModal();
        d.dispatchEvent(new Event("kr-command-open"));
      }
    },
  };
  Kairo.toast = toast;
  Kairo.confirm = confirm;
  Kairo.version = "0.1.0";
  window.Kairo = Kairo;
})(window);
